import type { ReactNode } from 'react';

import Modal from '@/components/Modal/Modal';
import { Button, ButtonVariation } from '@/components/Button';
import { ModalSize } from '@/utils';
import './ConfirmActionModal.css';

export interface ConfirmActionModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  description: ReactNode;
  confirmLabel: string;
  variant?: 'default' | 'rounded';
  onConfirm: () => Promise<void> | void;
  isConfirming?: boolean;
  cancelLabel?: string;
  successMessage?: string;
  'data-testid'?: string;
}

function ConfirmActionModal({
  isOpen,
  onClose,
  title,
  description,
  confirmLabel,
  variant = 'default',
  onConfirm,
  isConfirming = false,
  cancelLabel = 'Cancel',
  successMessage,
  'data-testid': dataTestId,
}: ConfirmActionModalProps) {
  const baseTestId = dataTestId ?? 'confirm-action-modal';

  const handleConfirm = async () => {
    await onConfirm();
    onClose();
  };

  return (
    <>
      <Modal
        className={`confirm-action-modal__wrapper confirm-action-modal__wrapper--${variant}`}
        data-testid={baseTestId}
        heading={title}
        isOpen={isOpen}
        preventDismiss={isConfirming}
        size={ModalSize.Fit}
        onClose={onClose}>
        <div
          className='confirm-action-modal__body'
          data-testid={`${baseTestId}-content`}>
          <p
            className='ds-u-margin-bottom--0'
            data-testid={`${baseTestId}-description`}>
            {description}
          </p>
          <div
            className='confirm-action-modal__buttons'
            data-testid={`${baseTestId}-actions`}>
            <Button
              isAutoWidth
              data-testid={`${baseTestId}-cancel`}
              disabled={isConfirming}
              type='button'
              variation={ButtonVariation.GHOST}
              onClick={onClose}>
              {cancelLabel}
            </Button>
            <Button
              isAutoWidth
              data-testid={`${baseTestId}-confirm`}
              disabled={isConfirming}
              type='button'
              variation={ButtonVariation.SOLID}
              onClick={handleConfirm}>
              {confirmLabel}
            </Button>
          </div>
        </div>
      </Modal>
      <div
        className='sr-only'
        data-testid={`${baseTestId}-announcement`}
        role='status'>
        {successMessage}
      </div>
    </>
  );
}

export default ConfirmActionModal;
